import cx from 'classnames'
import React, { ReactElement, useState } from 'react'
import { NotificationInterface } from '../../../store/common/reducer'
import { useDispatch } from 'react-redux'
import { readNotification } from '../../../store/common/actions'
import { openNotificationLink } from '../../../utils/appUtils'
import { useTranslation } from 'react-i18next'
import { IconButton, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { localDateTime } from '@/utils/formatUtils'
import styles from './styles.module.scss'

interface Props {
  notification: NotificationInterface
  readNotificationHandler: (e: any) => void
  deleteNotificationHandler: (e: any, id: string) => void
  editNotificationHandler?: (
    e: any,
    notification: NotificationInterface,
  ) => void
  navigateToFaqHandlerAsProp?: (
    e: any,
    notification: NotificationInterface,
  ) => void
  isEdit: boolean
  type?: string
  showViewMore?: boolean
}

export default function NotificationListItem({
  isEdit,
  notification,
  readNotificationHandler,
  deleteNotificationHandler,
  editNotificationHandler,
  navigateToFaqHandlerAsProp,
  showViewMore = false,
}: Props): ReactElement {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const [isExpanded, setIsExpanded] = useState(false)

  const onClickMore = (event: any) => {
    event.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  const navigateToFaqHandler = async (
    e: any,
    notification: NotificationInterface,
  ) => {
    e.preventDefault()
    if (!notification.is_read) {
      dispatch(
        readNotification(notification._id, notification.is_read.toString()),
      )
    }

    if (notification.link.trim().length > 0) {
      openNotificationLink(notification.link)
    }
  }

  return (
    <div className="notify-item">
      <div
        className={cx({
          'notify-indicate': true,
          active: !notification.is_read,
        })}
        data-id={notification._id}
        data-read={notification.is_read}
        onClick={readNotificationHandler}
      ></div>
      <div
        data-id={notification._id}
        data-read={notification.is_read}
        onClick={e =>
          navigateToFaqHandlerAsProp
            ? navigateToFaqHandlerAsProp(e, notification)
            : navigateToFaqHandler(e, notification)
        }
      >
        <Typography variant="notiHeader">{notification.title}</Typography>
        <div
          className={cx(styles.notificationContent, {
            [styles.drawerMode]: showViewMore,
          })}
        >
          {showViewMore ? (
            <>
              {isExpanded ? (
                <>
                  {notification.content_html ? (
                    <Typography
                      variant="notiText"
                      dangerouslySetInnerHTML={{
                        __html: notification.content_html,
                      }}
                    />
                  ) : (
                    <Typography variant="notiText">
                      {notification.content}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography
                  variant="notiText"
                  dangerouslySetInnerHTML={{ __html: notification.excerpt }}
                />
              )}
              {notification.content !== notification.excerpt && (
                <button
                  className="btn notification-view-more"
                  onClick={event => onClickMore(event)}
                >
                  {isExpanded ? t('view less') : t('view more')}
                </button>
              )}
            </>
          ) : (
            <>
              {notification.content_html ? (
                <Typography
                  variant="notiText"
                  dangerouslySetInnerHTML={{
                    __html: notification.content_html,
                  }}
                />
              ) : (
                <Typography variant="notiText">
                  {notification.content}
                </Typography>
              )}
            </>
          )}
        </div>
      </div>
      <div className="notify-time">
        {localDateTime(notification.updated_at)}
      </div>
      <IconButton
        className="btn"
        aria-label={t('Delete Notification')}
        onClick={e => deleteNotificationHandler(e, notification._id)}
        size="small"
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
      {isEdit && (
        <IconButton
          className="btn noti-edit"
          aria-label={t('Edit Notification')}
          onClick={e => editNotificationHandler(e, notification)}
          size="small"
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  )
}
